import type { TypedDocumentNode } from '@apollo/client'
import { gql } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import { Card, CardContent, Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useRouterState } from '@tanstack/react-router'
import { SFProfileBanner } from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import { profileNameFromLayoutMatches } from '@web/src/features/profile/profile-layout-head'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { FollowingSortEnum } from '@web/src/lib/graphql/types.generated'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { UsersIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type { FollowerPageQuery, FollowerPageQueryVariables } from 'types/graphql'
import { z } from 'zod'

const QUERY: TypedDocumentNode<FollowerPageQuery, FollowerPageQueryVariables> = gql`
  query FollowerPage($page: Int, $filter: FollowingPageFilter, $sortBy: FollowingSortEnum) {
    followers: followerPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        follower {
          ...ProfileInfo
        }
      }
      count
      perPage
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

type FollowingFollowersSort = 'DISTANCE' | 'OLDEST' | 'LATEST' | 'NAME'

type FollowingFollowersSearchInput = {
  name?: string
  page?: number
  sortBy?: FollowingFollowersSort
}

function resolveFollowingFollowersSearchParams(search: FollowingFollowersSearchInput): {
  page: number
  name?: string
  sortBy: FollowingFollowersSort
} {
  return {
    page: search.page ?? 1,
    name: search.name,
    sortBy: search.sortBy ?? 'NAME',
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId/following/followers')({
  component: PageComponent,
  validateSearch: z.object({
    name: z.string().optional(),
    page: z.coerce.number().optional(),
    sortBy: z.enum(['DISTANCE', 'OLDEST', 'LATEST', 'NAME']).optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        sortBy: 'NAME',
      }),
    ],
  },
  loaderDeps: ({ search }) => resolveFollowingFollowersSearchParams(search),
  head: ({ matches }) => {
    const profileName = profileNameFromLayoutMatches(matches)
    const title = profileName ? `${profileName}'s Followers | ${env.VITE_APP_NAME}` : `Followers | ${env.VITE_APP_NAME}`
    return seo({
      title,
      description: 'View profiles following you. Browse followers and search by name.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    })
  },
  loader: async ({ context, params, deps }) => {
    const { queryRef } = await queryAndPreload(context, QUERY, {
      variables: {
        page: deps.page,
        sortBy: deps.sortBy as FollowingSortEnum,
        filter: {
          profileId: params.profileId,
          name: deps.name,
        },
      },
    })
    return { queryRef }
  },
})

function PageComponent() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const { data } = useReadQuery(queryRef as QueryRef<FollowerPageQuery>)
  const search = Route.useSearch()
  const resolvedSearch = useMemo(() => resolveFollowingFollowersSearchParams(search), [search])
  const isLoading = useRouterState({ select: (s) => s.status === 'pending' })
  const [showLoading, setShowLoading] = useState(false)

  const formMethods = useForm({
    defaultValues: {
      name: search.name,
      sortBy: resolvedSearch.sortBy,
    },
  })

  useEffect(() => {
    if (!isLoading) {
      setShowLoading(false)
    }
  }, [isLoading])

  const navigate = Route.useNavigate()

  const handleFilter = ({ page, name, sortBy }: { page?: number; name?: string; sortBy?: string }): void => {
    setShowLoading(true)
    navigate({
      search: {
        page: page ?? resolvedSearch.page,
        sortBy: sortBy ?? resolvedSearch.sortBy,
        name: name ?? search.name,
      },
      replace: true,
    } as never)
  }

  const entryCount = data?.followers?.entries?.length ?? 0

  return (
    <div className="mx-auto w-full max-w-[90rem] px-0">
      <div className="flex w-full flex-col gap-4">
        <h1 className="sr-only">{t('Followers Listing')}</h1>

        <Form className="w-full" formMethods={formMethods} suppressHydrationWarning>
          <SearchExpand
            className="w-full"
            clearSearchLabel={t('Clear search')}
            fieldProps={{ className: 'basis-0 flex-1 min-w-0' }}
            loading={showLoading}
            name="name"
            onSearchChange={(value) => handleFilter({ name: value })}
            placeholder={t('Search followers...')}
            rightBarChildren={
              <div className="flex w-[min(100%,180px)] shrink-0 basis-[180px] items-start">
                <NativeSelectFormField
                  aria-label={t('Sort By')}
                  fieldProps={{ className: 'w-full max-w-full' }}
                  name="sortBy"
                  onValueChange={(e) => handleFilter({ sortBy: e.target.value })}
                  selectProps={{ className: 'w-full min-w-0' }}
                >
                  <option value="NAME">{t('Name')}</option>
                  <option value="LATEST">{t('Latest')}</option>
                  <option value="OLDEST">{t('Oldest')}</option>
                  <option value="DISTANCE">{t('Distance')}</option>
                </NativeSelectFormField>
              </div>
            }
            toggleFiltersLabel={t('Toggle filters')}
          />
        </Form>

        {entryCount > 0 ? (
          <div className={cn('grid w-full gap-2', entryCount > 1 ? 'lg:grid-cols-2' : 'grid-cols-1')}>
            {data?.followers?.entries.map((entry) => {
              return (
                <Card className="p-2" key={entry.id}>
                  <CardContent className="p-0">
                    <SFProfileBanner
                      lowerChild={`${entry?.follower?.location}`}
                      profile={entry.follower}
                      shape="rounded"
                      size="2xl"
                    />
                  </CardContent>
                </Card>
              )
            })}
          </div>
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <UsersIcon />
              </EmptyMedia>
              <EmptyTitle>
                {search.name
                  ? t('No followers found for "{{name}}"', { name: search.name })
                  : t('No one is following at this time')}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}

        <PaginationControl
          count={data?.followers?.count || 0}
          onPageChange={(page) => handleFilter({ page: page.page })}
          page={resolvedSearch.page}
          pageSize={data?.followers?.perPage}
        />
      </div>
    </div>
  )
}
